import { useState } from 'react';
import { BOT_TIERS, GAME_MODES, MAX_PLAYERS, type BotTier, type GameMode } from '@arena/shared';
import { playVsBots } from '../net/session';
import { useSettingsStore } from '../store/settingsStore';

export const MIN_BOTS = 1;
/** The host takes one seat. */
export const MAX_BOTS = MAX_PLAYERS - 1;
export const DEFAULT_BOTS = 3;

export const TIER_LABELS: Record<BotTier, string> = {
  dummy: 'Dummy',
  easy: 'Easy',
  normal: 'Normal',
  hard: 'Hard',
};

const MODE_LABELS: Record<GameMode, string> = { ffa: 'FFA', teams: 'Teams' };

interface Props {
  name: string;
  disabled: boolean;
  /** This panel's Start opened the pending connection: label it instead of the generic status. */
  connecting?: boolean;
  onStart?: () => void;
}

/** Home → Play vs Bots: pick count, difficulty and mode, then start a private practice room. */
export function PlayVsBotsPanel({ name, disabled, connecting = false, onStart }: Props) {
  const tier = useSettingsStore((s) => s.botTier);
  const setTier = useSettingsStore((s) => s.setBotTier);
  const [count, setCount] = useState(DEFAULT_BOTS);
  const [mode, setMode] = useState<GameMode>('ffa');

  return (
    <section className="bots-panel" aria-label="Play vs Bots">
      <label className="field">
        Bots: {count}
        <input
          type="range"
          min={MIN_BOTS}
          max={MAX_BOTS}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
        />
      </label>
      <div className="row" role="radiogroup" aria-label="Difficulty">
        {BOT_TIERS.map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={t === tier}
            onClick={() => setTier(t)}
          >
            {TIER_LABELS[t]}
          </button>
        ))}
      </div>
      <div className="row" role="radiogroup" aria-label="Mode">
        {GAME_MODES.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={m === mode}
            onClick={() => setMode(m)}
          >
            {MODE_LABELS[m]}
          </button>
        ))}
      </div>
      <button
        disabled={disabled}
        aria-busy={connecting}
        onClick={() => {
          onStart?.();
          void playVsBots(name, { count, tier, mode });
        }}
      >
        {connecting ? 'Connecting…' : 'Start'}
      </button>
    </section>
  );
}
